import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { Notification } from '../../lib/notifications-api';
import {
  Mail,
  UserCheck,
  UserX,
  Calendar,
  Edit,
  MessageSquare,
  AlertCircle,
  Bell,
} from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onClick?: () => void;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  invitation_sent: Mail,
  rsvp_received: UserCheck,
  rsvp_updated: UserX,
  event_reminder: Calendar,
  event_updated: Edit,
  guest_message: MessageSquare,
  system_alert: AlertCircle,
};

const CATEGORY_COLORS: Record<string, string> = {
  invitation_sent: 'bg-blue-100 text-blue-600',
  rsvp_received: 'bg-green-100 text-green-600',
  rsvp_updated: 'bg-yellow-100 text-yellow-600',
  event_reminder: 'bg-purple-100 text-purple-600',
  event_updated: 'bg-orange-100 text-orange-600',
  guest_message: 'bg-pink-100 text-pink-600',
  system_alert: 'bg-red-100 text-red-600',
};

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const Icon = CATEGORY_ICONS[notification.category] || Bell;
  const colorClass = CATEGORY_COLORS[notification.category] || 'bg-gray-100 text-gray-600';

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: ro,
  });

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors',
        !notification.isRead && 'bg-blue-50/50',
      )}
    >
      {/* Icon */}
      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0', colorClass)}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-sm line-clamp-2',
              notification.isRead ? 'text-gray-600' : 'text-gray-900 font-medium',
            )}
          >
            {notification.title}
          </p>
          {!notification.isRead && (
            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{notification.body}</p>
        <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
      </div>
    </button>
  );
}
